import _defineProperty from "@babel/runtime/helpers/defineProperty";
import React from 'react';
import { NAVIGATION_CHANNEL, OPERATIONAL_EVENT_TYPE } from '../utils/analytics';
import { withAnalyticsEvents } from '@atlaskit/analytics-next';
import { errorToReason } from '../utils/error-to-reason';
import { retryOnException } from '../utils/retry-operation';
import { withExperienceTracker } from '../utils/experience-tracker';
const DATA_PROVIDER_SUBJECT = 'atlassianSwitcherDataProvider';
export let Status = /*#__PURE__*/function (Status) {
  Status["LOADING"] = "loading";
  Status["COMPLETE"] = "complete";
  Status["ERROR"] = "error";
  return Status;
}({});
export const createResultComplete = data => ({
  status: Status.COMPLETE,
  data
});
export const isComplete = result => result.status === Status.COMPLETE;
export const isError = result => result.status === Status.ERROR;
export const isLoading = result => result.status === Status.LOADING;
export const hasLoaded = result => result.status !== Status.LOADING;
export default function (name, experienceMark, mapPropsToPromise, mapPropsToInitialValue, retryConfig, shouldUpdate) {
  const getInitialState = props => {
    if (mapPropsToInitialValue) {
      const initialValue = mapPropsToInitialValue(props);
      if (initialValue !== undefined) {
        return {
          status: Status.COMPLETE,
          data: initialValue
        };
      }
    }
    return {
      status: Status.LOADING,
      data: null
    };
  };
  class DataProvider extends React.Component {
    constructor(...args) {
      super(...args);
      _defineProperty(this, "acceptResults", true);
      _defineProperty(this, "state", getInitialState(this.props.params));
      _defineProperty(this, "fireOperationalEvent", payload => {
        if (this.props.createAnalyticsEvent) {
          this.props.createAnalyticsEvent({
            eventType: OPERATIONAL_EVENT_TYPE,
            actionSubject: DATA_PROVIDER_SUBJECT,
            ...payload,
            attributes: {
              ...payload.attributes,
              outdated: !this.acceptResults
            }
          }).fire(NAVIGATION_CHANNEL);
        }
      });
    }
    componentWillUnmount() {
      /**
       * Promise resolved after component is unmounted to be ignored
       */
      this.acceptResults = false;
    }
    componentDidMount() {
      this.props.experienceTracker.markStart(experienceMark);
      this.fetchData();
    }
    componentDidUpdate(prevProps) {
      if (shouldUpdate !== null && shouldUpdate !== void 0 && shouldUpdate(prevProps.params, this.props.params)) {
        this.fetchData();
      }
    }
    fetchData() {
      retryOnException(() => mapPropsToPromise(this.props.params), {
        intervalsMS: (retryConfig === null || retryConfig === void 0 ? void 0 : retryConfig.intervalsMS) || [],
        shouldRetryOnException: retryConfig === null || retryConfig === void 0 ? void 0 : retryConfig.shouldRetryOnException,
        onRetry: (previousException, retryCount) => {
          this.onRetry(previousException, retryCount);
        }
      }).then(result => {
        this.onResult(result);
      }).catch(error => {
        this.onError(error);
      });
    }
    onResult(value) {
      if (this.acceptResults) {
        this.setState({
          data: value,
          status: Status.COMPLETE
        });
      }
      this.props.experienceTracker.markEnd(experienceMark);
      this.fireOperationalEvent({
        action: 'receivedResult',
        actionSubjectId: name,
        attributes: {
          provider: name
        }
      });
    }
    onRetry(error, retryCount) {
      this.fireOperationalEvent({
        action: 'retried',
        actionSubjectId: name,
        attributes: {
          provider: name,
          reason: retryConfig !== null && retryConfig !== void 0 && retryConfig.customErrorToReason ? retryConfig.customErrorToReason(error) : errorToReason(error),
          retryCount
        }
      });
    }
    onError(error) {
      /**
       * Do not transition from "complete" state to "error"
       */
      if (this.acceptResults && !isComplete(this.state)) {
        this.setState({
          error,
          status: Status.ERROR,
          data: null
        });
      }
      this.fireOperationalEvent({
        action: 'failed',
        actionSubjectId: name,
        attributes: {
          provider: name,
          reason: errorToReason(error)
        }
      });
    }
    render() {
      return this.props.children(this.state);
    }
  }

  // In prefetch, we start with empty args {}, but when getting data from the cache in the data provider, extra args are injected by HOC withAnalyticsEvents and withExperienceTracker
  // This results in a cache miss due to a key mismatch.
  // The workaround here is to use a wrapper (OuterComponent) that passes the original props as a separate prop(params) to the underlying component(InnerComponent).
  // For more info: https://product-fabric.atlassian.net/browse/CXP-2697
  _defineProperty(DataProvider, "displayName", `DataProvider(${name})`);
  const InnerComponent = withAnalyticsEvents()(withExperienceTracker(DataProvider));
  const OuterComponent = ({
    children,
    ...params
  }) => {
    return /*#__PURE__*/React.createElement(InnerComponent, {
      params: params
    }, children);
  };
  return OuterComponent;
}